import axios from "axios";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import DataTable from "../components/DataTable";

const MODULO = "LegalizacionViaticos";

// ======================================================
// NORMALIZAR TEXTO
// ======================================================
const normalizar = (texto = "") =>
  String(texto)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "")
    .toLowerCase();

// ======================================================
// LEER PERMISOS DEL USUARIO
// ======================================================
const leerPermisos = () => {
  try {
    return JSON.parse(
      localStorage.getItem("permisos") ||
        localStorage.getItem("usuario") ||
        localStorage.getItem("user") ||
        localStorage.getItem("usuarios") ||
        "{}"
    );
  } catch {
    return {};
  }
};

// ======================================================
// BUSCAR MÓDULO DE PERMISOS
// ======================================================
const buscarModulo = (datos, nombreModulo) => {
  const modulos = Array.isArray(datos)
    ? datos
    : datos?.modulos ||
      datos?.permisos ||
      datos?.usuario?.modulos ||
      datos?.usuario?.permisos ||
      datos?.user?.modulos ||
      datos?.user?.permisos ||
      [];

  return (
    modulos.find((modulo) => {
      const nombre =
        modulo?.nombreModulo ||
        modulo?.modulo ||
        modulo?.nombre ||
        modulo?.moduloObjectId?.nombreModulo ||
        "";

      return normalizar(nombre) === normalizar(nombreModulo);
    }) || {}
  );
};

// ======================================================
// FORMATEAR FECHA DD/MM/AAAA
// ======================================================
const formatearFecha = (fecha) => {
  if (!fecha) return "-";

  const partes = String(fecha).split("T")[0].split("-");

  if (partes.length !== 3) return "-";

  return `${partes[2]}/${partes[1]}/${partes[0]}`;
};

// ======================================================
// COMPONENTE
// ======================================================
const ListarLegalizacionViaticos = ({ refresh }) => {
  const navigate = useNavigate();

  const { id: agendaId } = useParams();

  // ====================================================
  // ESTADOS GENERALES
  // ====================================================
  const [legalizaciones, setLegalizaciones] = useState([]);

  const [loading, setLoading] = useState(false);

  const [eliminandoId, setEliminandoId] = useState("");

  // ====================================================
  // ESTADOS DESCARGA
  // ====================================================
  const [
    descargandoLegalizacionId,
    setDescargandoLegalizacionId,
  ] = useState("");

  const [
    descargandoTransporteId,
    setDescargandoTransporteId,
  ] = useState("");

  const [
    descargandoDeclaracionId,
    setDescargandoDeclaracionId,
  ] = useState("");

  // ====================================================
  // PERMISOS
  // ====================================================
  const datosPermisos = leerPermisos();

  const rol = normalizar(
    datosPermisos?.rolObjectId?.nombreRol ||
      datosPermisos?.rol?.nombreRol ||
      datosPermisos?.rol?.nombre ||
      datosPermisos?.rol ||
      datosPermisos?.role ||
      datosPermisos?.usuario?.rol ||
      datosPermisos?.user?.rol ||
      ""
  );

  const esAdmin =
    rol.includes("superadmin") ||
    rol.includes("administrador");

  const modulo = buscarModulo(
    datosPermisos,
    MODULO
  );

  const puedeLeer =
    esAdmin ||
    modulo?.puedeLeer === true ||
    modulo?.leer === true ||
    modulo?.read === true ||
    modulo?.ver === true;

  const puedeCrear =
    esAdmin ||
    modulo?.puedeCrear === true ||
    modulo?.crear === true ||
    modulo?.create === true;

  const puedeActualizar =
    esAdmin ||
    modulo?.puedeActualizar === true ||
    modulo?.actualizar === true ||
    modulo?.editar === true ||
    modulo?.update === true;

  const puedeEliminar =
    esAdmin ||
    modulo?.puedeEliminar === true ||
    modulo?.eliminar === true ||
    modulo?.delete === true;

  // ====================================================
  // CONFIGURACIÓN AXIOS
  // ====================================================
  const config = {
    headers: {
      Authorization: `Bearer ${
        localStorage.getItem("token") ||
        sessionStorage.getItem("token") ||
        ""
      }`,
    },
  };

  // ====================================================
  // VALIDAR SESIÓN
  // ====================================================
  const validarSesion = (error) => {
    if (error?.response?.status !== 401) {
      return false;
    }

    Swal.fire({
      icon: "warning",
      title: "Sesión expirada",
      text: "Por favor inicia sesión nuevamente.",
      confirmButtonText: "Aceptar",
    }).then(() => {
      localStorage.removeItem("token");

      sessionStorage.removeItem("token");

      navigate("/login");
    });

    return true;
  };

  // ====================================================
  // CARGAR LEGALIZACIONES
  // ====================================================
  const cargarDatos = async () => {
    if (!puedeLeer || !agendaId) {
      setLegalizaciones([]);

      return;
    }

    try {
      setLoading(true);

      const { data } = await axios.get(
        `/api/legalizacionesviaticos/agenda/${agendaId}`,
        config
      );

      if (Array.isArray(data?.legalizaciones)) {
        setLegalizaciones(
          data.legalizaciones
        );
      } else if (data?.legalizacion) {
        setLegalizaciones([
          data.legalizacion,
        ]);
      } else {
        setLegalizaciones([]);
      }
    } catch (error) {
      if (validarSesion(error)) {
        return;
      }

      if (
        error?.response?.status === 404
      ) {
        setLegalizaciones([]);

        return;
      }

      console.error(
        "Error cargando la legalización:",
        error
      );

      Swal.fire(
        "Error",
        error?.response?.data?.message ||
          "No se pudo cargar la legalización.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  // ====================================================
  // EFECTO
  // ====================================================
  useEffect(() => {
    cargarDatos();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agendaId, refresh]);

  // ====================================================
  // OBTENER ID DE UNA REFERENCIA
  // ====================================================
  const obtenerIdReferencia = (valor) => {
    if (!valor) {
      return "";
    }

    if (
      typeof valor === "object" &&
      valor !== null
    ) {
      return valor._id || "";
    }

    return String(valor);
  };

  // ====================================================
  // LIMPIAR PARTE DEL NOMBRE ARCHIVO
  // ====================================================
  const limpiarParteNombreArchivo = (
    valor,
    fallback = ""
  ) => {
    const texto =
      String(valor ?? "").trim();

    if (!texto) {
      return fallback;
    }

    return (
      texto
        .replace(/[\\/:*?"<>|]/g, "_")
        .replace(/\s+/g, " ")
        .trim() || fallback
    );
  };

  // ====================================================
  // OBTENER DOCUMENTO INSTRUCTOR
  // ====================================================
  const obtenerDocumentoInstructor =
    async (legalizacion) => {
      // ==================================================
      // DESDE LEGALIZACIÓN
      // ==================================================
      let documentoInstructor =
        legalizacion?.documentoInstructor ||
        legalizacion
          ?.agendaViaticoObjectId
          ?.contrato
          ?.instructor
          ?.documentoInstructor ||
        legalizacion
          ?.agendaViaticoObjectId
          ?.contrato
          ?.documentoInstructor ||
        "";

      if (documentoInstructor) {
        return String(
          documentoInstructor
        ).trim();
      }

      // ==================================================
      // CONSULTAR AGENDA
      // ==================================================
      const agendaReferencia =
        legalizacion
          ?.agendaViaticoObjectId;

      const agendaReferenciaId =
        obtenerIdReferencia(
          agendaReferencia
        ) || agendaId;

      let agendaCargada =
        typeof agendaReferencia ===
          "object" &&
        agendaReferencia !== null
          ? agendaReferencia
          : null;

      if (
        agendaReferenciaId &&
        !agendaCargada
          ?.contrato
          ?.instructor
          ?.documentoInstructor
      ) {
        const responseAgenda =
          await axios.get(
            `/api/agendasviaticos/listid/${agendaReferenciaId}`,
            config
          );

        const dataAgenda =
          responseAgenda?.data || {};

        agendaCargada =
          dataAgenda?.agenda ||
          dataAgenda?.message ||
          agendaCargada;
      }

      // ==================================================
      // DESDE AGENDA
      // ==================================================
      documentoInstructor =
        agendaCargada
          ?.documentoInstructor ||
        agendaCargada
          ?.contrato
          ?.instructor
          ?.documentoInstructor ||
        agendaCargada
          ?.contrato
          ?.documentoInstructor ||
        "";

      if (documentoInstructor) {
        return String(
          documentoInstructor
        ).trim();
      }

      // ==================================================
      // CONSULTAR CONTRATO
      // ==================================================
      const contratoReferencia =
        agendaCargada?.contrato ||
        agendaReferencia?.contrato ||
        null;

      const contratoId =
        obtenerIdReferencia(
          contratoReferencia
        );

      if (contratoId) {
        const responseContrato =
          await axios.get(
            `/api/contratos/listid/${contratoId}`,
            config
          );

        const dataContrato =
          responseContrato?.data || {};

        const contratoCargado =
          dataContrato?.contrato ||
          dataContrato?.message ||
          null;

        documentoInstructor =
          contratoCargado
            ?.instructor
            ?.documentoInstructor ||
          contratoCargado
            ?.documentoInstructor ||
          "";
      }

      if (!documentoInstructor) {
        throw new Error(
          "No fue posible obtener el documento de identidad del instructor."
        );
      }

      return String(
        documentoInstructor
      ).trim();
    };

  // ====================================================
  // NOMBRE LEGALIZACIÓN
  // ====================================================
  const construirNombreLegalizacion = (
    numeroOrdenViaje,
    documentoInstructor
  ) => {
    const orden =
      limpiarParteNombreArchivo(
        numeroOrdenViaje
      );

    const documento =
      limpiarParteNombreArchivo(
        documentoInstructor
      );

    if (!orden) {
      throw new Error(
        "No fue posible obtener el número de orden de viaje."
      );
    }

    if (!documento) {
      throw new Error(
        "No fue posible obtener el documento de identidad del instructor."
      );
    }

    return `GCCON-F-101 (${orden}) - ${documento}.docx`;
  };

  // ====================================================
  // LEER ERROR DEVUELTO COMO BLOB
  // ====================================================
  const obtenerMensajeErrorBlob =
    async (
      error,
      mensajePorDefecto
    ) => {
      let mensaje =
        mensajePorDefecto;

      const responseData =
        error?.response?.data;

      if (
        responseData instanceof Blob
      ) {
        try {
          const texto =
            await responseData.text();

          if (texto) {
            try {
              const json =
                JSON.parse(texto);

              mensaje =
                json?.message ||
                mensaje;
            } catch {
              mensaje = texto;
            }
          }
        } catch {
          // mantener mensaje
        }
      } else if (
        error?.response?.data?.message
      ) {
        mensaje =
          error.response.data.message;
      } else if (
        error?.message
      ) {
        mensaje =
          error.message;
      }

      return mensaje;
    };

  // ====================================================
  // OBTENER NOMBRE DESDE CONTENT-DISPOSITION
  // ====================================================
  const obtenerNombreContentDisposition = (
    response,
    nombrePorDefecto
  ) => {
    const disposition =
      response?.headers?.[
        "content-disposition"
      ] || "";

    if (!disposition) {
      return nombrePorDefecto;
    }

    // ==================================================
    // filename*=UTF-8''
    // ==================================================
    const utf8Match =
      disposition.match(
        /filename\*=UTF-8''([^;]+)/i
      );

    if (utf8Match?.[1]) {
      try {
        return decodeURIComponent(
          utf8Match[1]
            .replace(/["']/g, "")
            .trim()
        );
      } catch {
        // continuar
      }
    }

    // ==================================================
    // filename=""
    // ==================================================
    const normalMatch =
      disposition.match(
        /filename="?([^"]+)"?/i
      );

    if (normalMatch?.[1]) {
      return normalMatch[1]
        .replace(/[";]/g, "")
        .trim();
    }

    return nombrePorDefecto;
  };

  // ====================================================
  // DESCARGAR BLOB
  // ====================================================
  const descargarBlob = (
    response,
    nombreArchivo
  ) => {
    const contentType =
      response.headers?.[
        "content-type"
      ] ||
      "application/octet-stream";

    const blob = new Blob(
      [response.data],
      {
        type: contentType,
      }
    );

    const url =
      window.URL.createObjectURL(
        blob
      );

    const enlace =
      document.createElement("a");

    enlace.href = url;

    enlace.download =
      nombreArchivo;

    document.body.appendChild(
      enlace
    );

    enlace.click();

    enlace.remove();

    setTimeout(() => {
      window.URL.revokeObjectURL(
        url
      );
    }, 1000);
  };

  // ====================================================
  // VALIDAR RESPUESTA DEL BACKEND
  // ====================================================
  const validarRespuestaBlob =
    async (
      response,
      mensajePorDefecto
    ) => {
      const contentType =
        response.headers?.[
          "content-type"
        ] || "";

      if (
        contentType.includes(
          "application/json"
        )
      ) {
        const texto =
          await response.data.text();

        let mensaje =
          mensajePorDefecto;

        if (texto) {
          try {
            const data =
              JSON.parse(texto);

            mensaje =
              data?.message ||
              mensaje;
          } catch {
            mensaje = texto;
          }
        }

        throw new Error(
          mensaje
        );
      }
    };

  // ====================================================
  // DESCARGAR LEGALIZACIÓN
  // ====================================================
  const descargarLegalizacion =
    async (legalizacion) => {
      if (!legalizacion?._id) {
        Swal.fire(
          "Error",
          "No se encontró el identificador de la legalización.",
          "error"
        );

        return;
      }

      try {
        setDescargandoLegalizacionId(
          legalizacion._id
        );

        console.log(
          "Generando legalización:",
          legalizacion._id
        );

        // ==================================================
        // BACKEND
        // ==================================================
        const response =
          await axios.get(
            `/api/legalizacion/generar-docx/${legalizacion._id}`,
            {
              ...config,
              responseType: "blob",
            }
          );

        await validarRespuestaBlob(
          response,
          "No se pudo generar la legalización."
        );

        // ==================================================
        // DOCUMENTO INSTRUCTOR
        // ==================================================
        const documentoInstructor =
          await obtenerDocumentoInstructor(
            legalizacion
          );

        const numeroOrdenViaje =
          legalizacion
            ?.numeroOrdenViaje ||
          "";

        // ==================================================
        // NOMBRE
        // ==================================================
        const nombreArchivo =
          construirNombreLegalizacion(
            numeroOrdenViaje,
            documentoInstructor
          );

        // ==================================================
        // DESCARGAR
        // ==================================================
        descargarBlob(
          response,
          nombreArchivo
        );

        Swal.fire({
          icon: "success",
          title:
            "Legalización descargada",
          text:
            "El documento fue generado correctamente.",
          showConfirmButton: false,
          timer: 1600,
        });
      } catch (error) {
        if (
          validarSesion(error)
        ) {
          return;
        }

        console.error(
          "Error descargando legalización:",
          error
        );

        const mensaje =
          await obtenerMensajeErrorBlob(
            error,
            "No se pudo descargar la legalización."
          );

        Swal.fire(
          "Error",
          mensaje,
          "error"
        );
      } finally {
        setDescargandoLegalizacionId(
          ""
        );
      }
    };

  // ====================================================
  // DESCARGAR TRANSPORTE INFORMAL
  // ====================================================
  const descargarTransporteInformal =
    async (legalizacion) => {
      if (!legalizacion?._id) {
        Swal.fire(
          "Error",
          "No se encontró el identificador de la legalización.",
          "error"
        );

        return;
      }

      try {
        setDescargandoTransporteId(
          legalizacion._id
        );

        console.log(
          "Generando transporte informal:",
          legalizacion._id
        );

        console.log(
          "URL:",
          `/api/transporte-informal/generar-excel/${legalizacion._id}`
        );

        // ==================================================
        // BACKEND
        // ==================================================
        const response =
          await axios.get(
            `/api/transporte-informal/generar-excel/${legalizacion._id}`,
            {
              ...config,
              responseType: "blob",
            }
          );

        // ==================================================
        // VALIDAR
        // ==================================================
        await validarRespuestaBlob(
          response,
          "No se pudo generar el formato de transporte informal."
        );

        // ==================================================
        // NOMBRE
        // ==================================================
        const nombreArchivo =
          obtenerNombreContentDisposition(
            response,
            `Transporte_Informal_${
              legalizacion.numeroOrdenViaje ||
              legalizacion._id
            }.xlsx`
          );

        // ==================================================
        // DESCARGAR
        // ==================================================
        descargarBlob(
          response,
          nombreArchivo
        );

        Swal.fire({
          icon: "success",
          title:
            "Transporte informal descargado",
          text:
            "El archivo Excel fue generado correctamente.",
          showConfirmButton: false,
          timer: 1600,
        });
      } catch (error) {
        if (
          validarSesion(error)
        ) {
          return;
        }

        console.error(
          "Error descargando transporte informal:",
          error
        );

        const mensaje =
          await obtenerMensajeErrorBlob(
            error,
            "No se pudo descargar el formato de transporte informal."
          );

        Swal.fire(
          "Error",
          mensaje,
          "error"
        );
      } finally {
        setDescargandoTransporteId(
          ""
        );
      }
    };

  // ====================================================
  // DESCARGAR DECLARACIÓN JURAMENTADA
  // ====================================================
  const descargarDeclaracionJuramentada =
    async (legalizacion) => {
      if (!legalizacion?._id) {
        Swal.fire(
          "Error",
          "No se encontró el identificador de la legalización.",
          "error"
        );

        return;
      }

      try {
        setDescargandoDeclaracionId(
          legalizacion._id
        );

        console.log(
          "Generando declaración juramentada:",
          legalizacion._id
        );

        console.log(
          "URL:",
          `/api/declaracion-juramentada/generar-docx/${legalizacion._id}`
        );

        // ==================================================
        // BACKEND
        //
        // IMPORTANTE:
        // La declaración está registrada dentro de
        // generar_declaracionjuramentada.route.js
        //
        // /api/declaracion-juramentada/generar-docx/:id
        // ==================================================
        const response =
          await axios.get(
            `/api/declaracion-juramentada/generar-docx/${legalizacion._id}`,
            {
              ...config,
              responseType: "blob",
            }
          );

        // ==================================================
        // VALIDAR
        // ==================================================
        await validarRespuestaBlob(
          response,
          "No se pudo generar la declaración juramentada."
        );

        // ==================================================
        // NOMBRE
        // ==================================================
        const nombreArchivo =
          obtenerNombreContentDisposition(
            response,
            `Declaracion_Juramentada_${
              legalizacion.numeroOrdenViaje ||
              legalizacion._id
            }.docx`
          );

        // ==================================================
        // DESCARGAR
        // ==================================================
        descargarBlob(
          response,
          nombreArchivo
        );

        Swal.fire({
          icon: "success",
          title:
            "Declaración descargada",
          text:
            "La declaración juramentada fue generada correctamente.",
          showConfirmButton: false,
          timer: 1600,
        });
      } catch (error) {
        if (
          validarSesion(error)
        ) {
          return;
        }

        console.error(
          "Error descargando declaración juramentada:",
          error
        );

        const mensaje =
          await obtenerMensajeErrorBlob(
            error,
            "No se pudo descargar la declaración juramentada."
          );

        Swal.fire(
          "Error",
          mensaje,
          "error"
        );
      } finally {
        setDescargandoDeclaracionId(
          ""
        );
      }
    };

  // ====================================================
  // ELIMINAR LEGALIZACIÓN
  // ====================================================
  const eliminarLegalizacion =
    async (legalizacionId) => {
      if (!puedeEliminar) {
        Swal.fire(
          "Sin permisos",
          "No tiene permisos para eliminar legalizaciones.",
          "warning"
        );

        return;
      }

      const confirmacion =
        await Swal.fire({
          title:
            "¿Eliminar legalización?",

          text:
            "También se eliminarán las fotografías y asistencias.",

          icon:
            "warning",

          showCancelButton:
            true,

          confirmButtonText:
            "Sí, eliminar",

          cancelButtonText:
            "Cancelar",

          confirmButtonColor:
            "#d33",
        });

      if (
        !confirmacion.isConfirmed
      ) {
        return;
      }

      try {
        setEliminandoId(
          legalizacionId
        );

        const { data } =
          await axios.delete(
            `/api/legalizacionesviaticos/${legalizacionId}`,
            config
          );

        setLegalizaciones(
          (actuales) =>
            actuales.filter(
              (item) =>
                item._id !==
                legalizacionId
            )
        );

        Swal.fire({
          icon:
            "success",

          title:
            data?.message ||
            "Legalización eliminada",

          showConfirmButton:
            false,

          timer:
            1500,
        });
      } catch (error) {
        if (
          validarSesion(error)
        ) {
          return;
        }

        console.error(
          "Error eliminando la legalización:",
          error
        );

        Swal.fire(
          "Error",
          error?.response?.data?.message ||
            "No se pudo eliminar la legalización.",
          "error"
        );
      } finally {
        setEliminandoId(
          ""
        );
      }
    };

  // ====================================================
  // DATOS DE LA TABLA
  // ====================================================
  const dataTabla =
    legalizaciones.map(
      (item, index) => {
        const agenda =
          item.agendaViaticoObjectId ||
          {};

        // ==================================================
        // ESTADOS
        // ==================================================
        const eliminando =
          eliminandoId ===
          item._id;

        const descargandoLegalizacion =
          descargandoLegalizacionId ===
          item._id;

        const descargandoTransporte =
          descargandoTransporteId ===
          item._id;

        const descargandoDeclaracion =
          descargandoDeclaracionId ===
          item._id;

        const procesando =
          eliminando ||
          descargandoLegalizacion ||
          descargandoTransporte ||
          descargandoDeclaracion;

        return {
          // ==================================================
          // ITEM
          // ==================================================
          item:
            index + 1,

          // ==================================================
          // ORDEN
          // ==================================================
          orden: (
            <span className="small fw-semibold">
              {item.numeroOrdenViaje ||
                "Sin número"}
            </span>
          ),

          // ==================================================
          // AGENDA
          // ==================================================
          agenda: (
            <div
              style={{
                minWidth:
                  180,
              }}
            >
              <div className="fw-semibold">
                {agenda.entidad ||
                  "Sin entidad"}
              </div>

              {agenda.ruta && (
                <small className="text-secondary">
                  {agenda.ruta}
                </small>
              )}
            </div>
          ),

          // ==================================================
          // DESPLAZAMIENTO
          // ==================================================
          desplazamiento: (
            <div
              className="small"
              style={{
                minWidth:
                  120,
              }}
            >
              <div>
                <span className="text-secondary">
                  Inicio:
                </span>{" "}

                <strong>
                  {formatearFecha(
                    agenda.fechaInicioDesplazamiento
                  )}
                </strong>
              </div>

              <div>
                <span className="text-secondary">
                  Fin:
                </span>{" "}

                <strong>
                  {formatearFecha(
                    agenda.fechaFinDesplazamiento
                  )}
                </strong>
              </div>
            </div>
          ),

          // ==================================================
          // PRESENTACIÓN
          // ==================================================
          presentacion: (
            <div
              style={{
                minWidth:
                  120,
              }}
            >
              <div className="fw-semibold">
                {item.ciudadPresentacion ||
                  "Sin ciudad"}
              </div>

              <small className="text-secondary">
                {formatearFecha(
                  item.fechaPresentacion
                )}
              </small>
            </div>
          ),

          // ==================================================
          // ACCIONES
          // ==================================================
          acciones: (
            <>
              {/* ===============================================
                  ESCRITORIO
              =============================================== */}
              <div className="d-flex gap-2 justify-content-end flex-nowrap">

                {/* =============================================
                    LEGALIZACIÓN
                ============================================= */}
                <button
                  type="button"
                  className="btn btn-outline-success btn-sm d-inline-flex align-items-center justify-content-center flex-shrink-0"
                  style={{
                    width:
                      34,

                    height:
                      34,
                  }}
                  title="Generar legalización"
                  disabled={
                    loading ||
                    procesando
                  }
                  onClick={() =>
                    descargarLegalizacion(
                      item
                    )
                  }
                >
                  <i
                    className={`fa-solid ${
                      descargandoLegalizacion
                        ? "fa-spinner fa-spin"
                        : "fa-file-word"
                    }`}
                  ></i>
                </button>

                {/* =============================================
                    TRANSPORTE INFORMAL
                ============================================= */}
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm d-inline-flex align-items-center justify-content-center flex-shrink-0"
                  style={{
                    width:
                      34,

                    height:
                      34,
                  }}
                  title="Generar transporte informal"
                  disabled={
                    loading ||
                    procesando
                  }
                  onClick={() =>
                    descargarTransporteInformal(
                      item
                    )
                  }
                >
                  <i
                    className={`fa-solid ${
                      descargandoTransporte
                        ? "fa-spinner fa-spin"
                        : "fa-file-excel"
                    }`}
                  ></i>
                </button>

                {/* =============================================
                    DECLARACIÓN JURAMENTADA
                ============================================= */}
                <button
                  type="button"
                  className="btn btn-outline-dark btn-sm d-inline-flex align-items-center justify-content-center flex-shrink-0"
                  style={{
                    width:
                      34,

                    height:
                      34,
                  }}
                  title="Generar declaración juramentada"
                  disabled={
                    loading ||
                    procesando
                  }
                  onClick={() =>
                    descargarDeclaracionJuramentada(
                      item
                    )
                  }
                >
                  <i
                    className={`fa-solid ${
                      descargandoDeclaracion
                        ? "fa-spinner fa-spin"
                        : "fa-file-signature"
                    }`}
                  ></i>
                </button>

                {/* =============================================
                    EDITAR
                ============================================= */}
                {puedeActualizar && (
                  <button
                    type="button"
                    className="btn btn-warning btn-sm d-inline-flex align-items-center justify-content-center flex-shrink-0"
                    style={{
                      width:
                        34,

                      height:
                        34,
                    }}
                    title="Editar"
                    disabled={
                      loading ||
                      procesando
                    }
                    onClick={() =>
                      navigate(
                        `/instructor/legalizacionviaticos/update/${item._id}`
                      )
                    }
                  >
                    <i className="fa-solid fa-pen-to-square"></i>
                  </button>
                )}

                {/* =============================================
                    ELIMINAR
                ============================================= */}
                {puedeEliminar && (
                  <button
                    type="button"
                    className="btn btn-danger btn-sm d-inline-flex align-items-center justify-content-center flex-shrink-0"
                    style={{
                      width:
                        34,

                      height:
                        34,
                    }}
                    title="Eliminar"
                    disabled={
                      loading ||
                      procesando
                    }
                    onClick={() =>
                      eliminarLegalizacion(
                        item._id
                      )
                    }
                  >
                    <i
                      className={`fa-solid ${
                        eliminando
                          ? "fa-spinner fa-spin"
                          : "fa-trash"
                      }`}
                    ></i>
                  </button>
                )}
              </div>

            </>
          ),
        };
      }
    );

  // ====================================================
  // COLUMNAS
  // ====================================================
  const columns = [
    {
      Header:
        "#",

      accessor:
        "item",
    },

    {
      Header:
        "Orden",

      accessor:
        "orden",
    },

    {
      Header:
        "Agenda",

      accessor:
        "agenda",
    },

    {
      Header:
        "Desplazamiento",

      accessor:
        "desplazamiento",
    },

    {
      Header:
        "Presentación",

      accessor:
        "presentacion",
    },

    {
      Header:
        "Acciones",

      accessor:
        "acciones",
    },
  ];

  // ====================================================
  // SIN PERMISO DE LECTURA
  // ====================================================
  if (!puedeLeer) {
    return (
      <div className="container-fluid px-2 px-md-4">
        <div className="alert alert-warning">
          <i className="fa-solid fa-lock me-2"></i>

          No tiene permisos para consultar las legalizaciones.
        </div>
      </div>
    );
  }

  // ====================================================
  // RENDER
  // ====================================================
  return (
    <div>
      <div className="container-fluid px-2 px-md-4">
        <div className="bg-white border rounded-4 shadow-sm overflow-hidden">

          {/* ==================================================
              ENCABEZADO
          ================================================== */}
          <div className="px-3 px-md-4 py-3 border-bottom bg-white">

            <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">

              {/* ==============================================
                  TÍTULO
              ============================================== */}
              <div>
                <h5 className="mt-2 mb-1 fw-semibold">
                  Legalización de Viáticos
                </h5>

                <small className="text-secondary">
                  Gestión de legalización de agendas de viajes.
                </small>
              </div>

              {/* ==============================================
                  BOTONES SUPERIORES
              ============================================== */}
              <div className="d-flex gap-2 flex-nowrap overflow-auto">

                {/* ============================================
                    VOLVER
                ============================================ */}
                <button
                  type="button"
                  className="btn btn-outline-dark text-nowrap flex-shrink-0"
                  title="Volver"
                  onClick={() => {
                    if (window.history.state?.idx > 0) {
                      navigate(-1);
                      return;
                    }
                    const contrato = legalizaciones[0]?.agendaViaticoObjectId?.contrato;
                    const contratoId = obtenerIdReferencia(contrato);
                    navigate(contratoId ? `/instructor/agendas/${contratoId}` : '/instructor', { replace: true });
                  }}
                >
                  <i className="fa-solid fa-arrow-rotate-left me-2"></i>

                  Volver
                </button>

                {/* ============================================
                    ACTUALIZAR
                ============================================ */}
                <button
                  type="button"
                  className="btn btn-outline-secondary text-nowrap flex-shrink-0"
                  disabled={
                    loading
                  }
                  onClick={
                    cargarDatos
                  }
                >
                  <i
                    className={`fa-solid ${
                      loading
                        ? "fa-spinner fa-spin"
                        : "fa-rotate"
                    } me-2`}
                  ></i>

                  Actualizar
                </button>

                {/* ============================================
                    NUEVA LEGALIZACIÓN
                ============================================ */}
                {puedeCrear &&
                  agendaId && (
                    <button
                      type="button"
                      className="btn btn-dark"
                      disabled={
                        loading ||
                        legalizaciones.length >
                          0
                      }
                      onClick={() =>
                        navigate(
                          `/instructor/legalizacionviaticos/nuevo/${agendaId}`
                        )
                      }
                      title={
                        legalizaciones.length >
                        0
                          ? "Esta agenda ya tiene una legalización"
                          : "Nueva legalización"
                      }
                    >
                      <i className="fa-solid fa-plus me-2"></i>

                      Nuevo
                    </button>
                  )}
              </div>
            </div>
          </div>

          {/* ==================================================
              CONTENIDO
          ================================================== */}
          <div>
            {loading ? (
              <div className="text-center text-secondary py-5">

                <div
                  className="spinner-border"
                  role="status"
                  aria-hidden="true"
                ></div>

                <div className="mt-3">
                  Cargando legalización…
                </div>
              </div>
            ) : dataTabla.length >
              0 ? (
              <div className="overflow-hidden">
                <DataTable
                  columns={
                    columns
                  }
                  data={
                    dataTabla
                  }
                />
              </div>
            ) : (
              <div className="text-center text-secondary py-5">

                <div className="display-6 mb-2">
                  📄
                </div>

                <div className="fw-semibold">
                  No hay una legalización registrada
                </div>

                <div className="small mb-3">
                  Esta agenda todavía no tiene una legalización.
                </div>

                {puedeCrear &&
                  agendaId && (
                    <button
                      type="button"
                      className="btn btn-dark"
                      onClick={() =>
                        navigate(
                          `/instructor/legalizacionviaticos/nuevo/${agendaId}`
                        )
                      }
                    >
                      <i className="fa-solid fa-plus me-2"></i>

                      Crear legalización
                    </button>
                  )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ======================================================
// EXPORTAR
// ======================================================
export default ListarLegalizacionViaticos;
